import moment from "moment";
import React from "react";
import { Table } from "reactstrap";
import numeral from "numeral";
import ReactTooltip from "react-tooltip";
import { useHistory } from "react-router-dom";

type RowItemProps = {
  row: any;
};

const RowItem = ({ row }: RowItemProps) => {
  const history = useHistory();
  const rentTime = () => {
    if (!row.available_from || !row.available_to) return "";
    return moment(row.available_to).diff(moment(row.available_from), "months");
  };
  const paymentTerm = row.payment_terms.find((el: any) => {
    const time = moment(new Date(), "YYYY-MM-DD");
    const start = moment(el.from_date);
    const end = moment(el.to_date);
    return time.isBetween(start, end);
  });
  const revenueSharePercentage = paymentTerm?.revenue_share_percentage
    ? paymentTerm?.revenue_share_percentage * 100
    : "";
  const increaseFeeRate = paymentTerm?.increase_fee_rate
    ? paymentTerm?.increase_fee_rate * 100
    : "";
  return (
    <tr
      className="normal-button"
      onClick={() => {
        history.push(`/finance/ifrs/${row.id}`);
      }}
    >
      <td>
        <div>#{row.contract_code}</div>
      </td>
      <td>
        <div>{row.internal_code}</div>
      </td>
      <td>
        <div>{row.rental_entity_tax_number}</div>
      </td>
      <td>
        <div>{row.rental_entity_name}</div>
      </td>
      <td>
        <div>{row.contract_purpose?.name}</div>
      </td>
      <td>
        <div>
          từ {row.available_from} đến {row.available_to}
        </div>
      </td>
      <td>
        <div>{rentTime()}</div>
      </td>
      <td>
        <div>{paymentTerm?.contract_payment_calculation_type?.name}</div>
      </td>
      <td>
        <div>{revenueSharePercentage}</div>
      </td>
      <td>
        <div>
          {paymentTerm?.rental_fee_with_tax
            ? numeral(paymentTerm?.rental_fee_with_tax).format("0,0")
            : ""}
        </div>
      </td>
      <td>
        <div>{increaseFeeRate}</div>
      </td>
      <td>
        <div>
          {row?.deposit_fee ? numeral(row?.deposit_fee).format("0,0") : ""}
        </div>
      </td>
      <td>
        <div>{row.contract_deposit_type?.name}</div>
      </td>
      <td>
        <div>
          {row?.management_fee
            ? numeral(row?.management_fee).format("0,0")
            : ""}
        </div>
      </td>
      <td>
        <div>
          {row.right_of_use_and_control__alter_design_reallocate
            ? "Có"
            : "Không"}
        </div>
      </td>
      <td>
        <div>{row.right_to_purchase_leased_asset ? "Có" : "Không"}</div>
      </td>
      <td>
        <div>
          {row?.maintenance_fee
            ? numeral(row?.maintenance_fee).format("0,0")
            : ""}
        </div>
      </td>
      <td>
        <div>{row.right_to_sublease ? "Có" : "Không"}</div>
      </td>
      <td>
        <div>{row.has_early_contract_termination_terms ? "Có" : "Không"}</div>
      </td>
      <td>
        <div>
          {row?.estimated_dismantling_cost_value
            ? numeral(row?.estimated_dismantling_cost_value).format("0,0")
            : ""}
        </div>
      </td>
      <td>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          {row.is_terminated && (
            <div
              className="ifrs-terminated-wrapper"
              data-for={`terminated-${row.id}`}
              data-tip={`Terminated on ${moment(row.terminated_on).format(
                "YYYY-MM-DD"
              )}<br>Reason: ${row?.contract_termination_reason?.name}`}
            >
              <span>Terminated</span>
            </div>
          )}
          {row.is_terminated && (
            <ReactTooltip
              type="dark"
              effect="solid"
              id={`terminated-${row.id}`}
              multiline
            />
          )}
          {new Date(row.contract_valid_to).getTime() < new Date().getTime() && (
            <div className="ifrs-contract-expire">
              <span>Hợp đồng hết hạn</span>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

type ContractTableProps = {
  data: Array<any>;
};

const ContractTable = ({ data }: ContractTableProps) => {
  const renderRow = (row: any, index: number) => {
    return <RowItem row={row} key={row.id || index} />;
  };
  return (
    <div className="contract-table__container">
      <Table responsive striped>
        <thead>
          <tr>
            <th>
              <div className="table-header">
                <span>Số HĐ</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Mã số cửa hàng</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Mã số thuế người cho thuê</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Tên người cho thuê</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Mục đích thuê</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Thời gian thuê</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Thời hạn thuê</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Cách thức tính tiền thuê</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Phần trăm doanh thu tính tiền thuê</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Tiền thuê cố đinh (sau thuế)</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Phần trăm tăng giá (nếu có)</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Phí đặt cọc</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Phương thức giải quyết khi đặt cọc</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Phí quản lý</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Quyền sử dụng và kiểm soát tài sản thuê</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>
                  Quyền mua lại tài sản thuê khi hết hạn hợp đồng thuê
                </span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Chi phí bảo trì (nếu có)</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Điều khoản cho thuê lại</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Điều khoản chấm dứt hợp đồng</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Chi phí tháo dỡ</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Thông tin khác</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>{data?.map?.(renderRow)}</tbody>
      </Table>
    </div>
  );
};

export default ContractTable;
